/**
 * HighlightText — case-insensitive substring highlighter.
 *
 * Wraps a plain-string body with `<mark>` runs around every occurrence
 * of the query. Used inside fuzzy-search result rows, command-palette
 * hits, threat-crossmap matches, and any list that highlights a typed
 * query.
 *
 * Single responsibility: render the highlighted spans. The component
 * intentionally accepts a *string* (not arbitrary nodes) so highlight
 * indices stay well-defined — composing it with rich-text content
 * lives at the call site.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo } from "react";
import { WARN } from "../design-system/tokens.js";
import { cn } from "../styling/index.js";

export interface HighlightTextProps {
  /** Source text. */
  readonly text: string;
  /** Substring(s) to highlight. Empty / whitespace queries pass through. */
  readonly query: string | readonly string[];
  /** Optional highlight foreground color. */
  readonly color?: string;
  /** Optional highlight background color. Defaults to `WARN[100]`. */
  readonly background?: string;
  /** Additional className applied to each `<mark>` segment. */
  readonly markClassName?: string;
  /** Additional className applied to the surrounding span. */
  readonly className?: string;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function collectQueries(query: string | readonly string[]): readonly string[] {
  const raw = typeof query === "string" ? [query] : query;
  const filtered: string[] = [];
  const seen = new Set<string>();
  for (const candidate of raw) {
    const trimmed = candidate.trim();
    if (trimmed.length > 0 && !seen.has(trimmed)) {
      seen.add(trimmed);
      filtered.push(trimmed);
    }
  }
  // Sort by descending length so "most specific wins" — overlapping
  // queries like `"a"` and `"ab"` always highlight the longer match.
  return filtered.sort((a, b) => b.length - a.length);
}

/** Splits `text` into alternating literal / matched chunks. */
function tokenize(
  text: string,
  queries: readonly string[],
): readonly { match: boolean; value: string }[] {
  if (queries.length === 0 || text.length === 0) {
    return [{ match: false, value: text }];
  }
  const pattern = new RegExp(`(${queries.map(escapeRegExp).join("|")})`, "ig");
  const segments: { match: boolean; value: string }[] = [];
  let cursor = 0;
  for (const match of text.matchAll(pattern)) {
    const index = match.index;
    if (index > cursor) {
      segments.push({ match: false, value: text.slice(cursor, index) });
    }
    segments.push({ match: true, value: match[0] });
    cursor = index + match[0].length;
  }
  if (cursor < text.length) {
    segments.push({ match: false, value: text.slice(cursor) });
  }
  return segments;
}

/** Renders text with every query match wrapped in a `<mark>`. */
export const HighlightText = React.memo(function HighlightText({
  text,
  query,
  color,
  background = WARN[100],
  markClassName,
  className,
}: HighlightTextProps): React.JSX.Element {
  const queries = useMemo(() => collectQueries(query), [query]);
  const segments = useMemo(() => tokenize(text, queries), [text, queries]);
  const markStyle = useMemo<React.CSSProperties>(
    () => ({ background, color, padding: 0 }),
    [background, color],
  );
  return (
    <span data-csk-highlight-text className={className}>
      {segments.map((segment, index) =>
        segment.match ? (
          <mark
            key={index}
            data-csk-highlight-match
            className={cn(markClassName)}
            style={markStyle}
          >
            {segment.value}
          </mark>
        ) : (
          <React.Fragment key={index}>{segment.value}</React.Fragment>
        ),
      )}
    </span>
  );
});
